import type { ActivityContext, ActivityHandle } from '../core/activity';
import './learn-arithmetic.css';

export interface LearnArithmeticLevelData {
  questionsArray: string[];
  circlesModel: number;
  randomOrder: boolean;
}

export interface LearnArithmeticLevel {
  level: number;
  objective: string;
  difficulty: number;
  data: LearnArithmeticLevelData[];
}

export interface LearnArithmeticLevelsFile {
  sourceActivity: string;
  sourceLicense: string;
  sourceProject: string;
  convertedAt: string;
  levels: LearnArithmeticLevel[];
}

export interface LearnArithmeticRunnerOptions {
  activityId: string;
  title: string;
  levels: LearnArithmeticLevel[];
}

function evalSimple(expr: string): number {
  const m = expr.replace(/\s+/g, '').match(/^(-?\d+)([+\-−×÷*/])(-?\d+)$/);
  if (!m) throw new Error(`Expressão inválida: ${expr}`);
  const a = parseInt(m[1]!, 10);
  const b = parseInt(m[3]!, 10);
  const op = m[2]!;
  if (op === '+') return a + b;
  if (op === '-' || op === '−') return a - b;
  if (op === '×' || op === '*') return a * b;
  return a / b;
}

function displayExpr(raw: string): string {
  return raw
    .replace(/-/g, '−')
    .replace(/\*/g, '×')
    .replace(/\//g, '÷');
}

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

export function runLearnArithmeticActivity(
  ctx: ActivityContext,
  opts: LearnArithmeticRunnerOptions,
): ActivityHandle {
  const { activityId, title, levels } = opts;
  const level = levels.find((l) => l.level === ctx.level) ?? levels[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }
  const config = level.data[0]!;
  const queue = config.randomOrder ? shuffle(config.questionsArray) : [...config.questionsArray];
  const total = queue.length;
  let current = 0;
  let correct = 0;
  let filledSet = new Set<number>();
  let locked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">${title}</div>
      <div class="activity-bar__level">Nível ${level.level} de ${levels.length}</div>
    </header>
    <section class="activity-stage learn-arith">
      <div class="feedback" aria-live="polite"></div>
      <div class="learn-arith__question"></div>
      <div class="learn-arith__progress"></div>
      <p class="learn-arith__hint">Clique nos círculos para representar o resultado, depois <strong>OK</strong>.</p>
      <div class="learn-arith__circles" role="group" aria-label="Círculos para contar"></div>
      <div class="learn-arith__actions">
        <button class="btn btn--primary learn-arith__ok" type="button">OK</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const backBtn = page.querySelector<HTMLButtonElement>('.activity-bar__back')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const questionEl = page.querySelector<HTMLElement>('.learn-arith__question')!;
  const progressEl = page.querySelector<HTMLElement>('.learn-arith__progress')!;
  const circlesEl = page.querySelector<HTMLElement>('.learn-arith__circles')!;
  const okBtn = page.querySelector<HTMLButtonElement>('.learn-arith__ok')!;
  backBtn.addEventListener('click', () => ctx.onExit());
  okBtn.addEventListener('click', validate);

  renderCircles();
  showQuestion();

  function renderCircles(): void {
    circlesEl.innerHTML = '';
    for (let i = 0; i < config.circlesModel; i++) {
      const btn = document.createElement('button');
      btn.className = 'learn-arith__circle';
      btn.type = 'button';
      btn.dataset.idx = String(i);
      btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', () => toggleCircle(i));
      circlesEl.appendChild(btn);
    }
  }

  function toggleCircle(idx: number): void {
    if (locked) return;
    const btn = circlesEl.querySelector<HTMLButtonElement>(`[data-idx="${idx}"]`)!;
    if (filledSet.has(idx)) {
      filledSet.delete(idx);
      btn.classList.remove('learn-arith__circle--filled');
      btn.setAttribute('aria-pressed', 'false');
    } else {
      filledSet.add(idx);
      btn.classList.add('learn-arith__circle--filled');
      btn.setAttribute('aria-pressed', 'true');
    }
    ctx.audio.play('click');
  }

  function showQuestion(): void {
    const expr = queue[current]!;
    questionEl.textContent = displayExpr(expr);
    progressEl.textContent = `${current + 1} / ${total}`;
    filledSet.clear();
    Array.from(circlesEl.children).forEach((c) => {
      const b = c as HTMLButtonElement;
      b.classList.remove('learn-arith__circle--filled', 'learn-arith__circle--ok', 'learn-arith__circle--err');
      b.setAttribute('aria-pressed', 'false');
    });
    locked = false;
  }

  function validate(): void {
    if (locked) return;
    const expr = queue[current]!;
    const expected = evalSimple(expr);
    const given = filledSet.size;
    if (given === expected) {
      correct++;
      locked = true;
      ctx.audio.play('correct');
      showFeedback(`✓ ${displayExpr(expr)} = ${expected}`, 'ok');
      filledSet.forEach((idx) => {
        circlesEl.querySelector<HTMLElement>(`[data-idx="${idx}"]`)!.classList.add('learn-arith__circle--ok');
      });
      window.setTimeout(() => {
        current++;
        if (current >= total) finishLevel();
        else showQuestion();
      }, 800);
    } else {
      locked = true;
      ctx.audio.play('wrong');
      showFeedback(`✗ Tente de novo`, 'err');
      filledSet.forEach((idx) => {
        circlesEl.querySelector<HTMLElement>(`[data-idx="${idx}"]`)!.classList.add('learn-arith__circle--err');
      });
      window.setTimeout(() => {
        filledSet.forEach((idx) => {
          const b = circlesEl.querySelector<HTMLElement>(`[data-idx="${idx}"]`)!;
          b.classList.remove('learn-arith__circle--err');
        });
        locked = false;
      }, 700);
    }
  }

  function showFeedback(text: string, kind: 'ok' | 'err'): void {
    feedback.textContent = text;
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 700);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level!.level, correct);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level!.level} concluído!</h2>
      <p class="activity-finish__text">Você acertou ${correct} de ${total}.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level!.level < levels.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/${activityId}/${level!.level + 1}`;
      });
    }
  }

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
